/**
 * Asset Maturity Card (DC-1367)
 *
 * Shows score gauge with improvement guidance.
 * Extracted from StrategicIntelligenceDashboard.tsx.
 */

import React from 'react';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import type { TechnicalAssetScore, BridgeIntelligence } from './types';
import { getScoreColor, generateScoreImprovementGuidance } from './helpers';

interface AssetMaturityCardProps {
  score: TechnicalAssetScore | null;
  guidance?: BridgeIntelligence['score_improvement_guidance'];
}

export const AssetMaturityCard: React.FC<AssetMaturityCardProps> = ({ score, guidance }) => {
  const fallback = generateScoreImprovementGuidance(score);
  const target = guidance?.target_score ?? fallback.target;
  const targetRating = guidance?.target_rating ?? fallback.targetRating;
  const summary = guidance?.summary ?? fallback.summary;

  const currentScore = score?.overall_score || 0;
  const hasAIGuidance = !!guidance?.summary;

  const openGuide = async () => {
    try {
      await fetch('/open-guide', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guidePath: 'Asset_Maturity_Card_Guide.md' })
      });
    } catch (err) {
      console.error('[AssetMaturity] Failed to open guide:', err);
    }
  };

  return (
    <div className="status-card status-card--asset">
      <div className="status-card-header">
        <span className="status-card-icon">📊</span>
        <h3 className="status-card-title">Asset Maturity</h3>
        <span className="status-card-badge">MECE v{score?.version || '3.0'}</span>
        <button
          className="help-button"
          onClick={openGuide}
          title="Open Asset Maturity Guide"
          aria-label="Help: Asset Maturity"
        >
          ❓
        </button>
      </div>

      <div className="status-card-body">
        <div className="asset-gauge">
          <CircularProgressbar
            value={currentScore}
            text={`${Math.round(currentScore)}`}
            strokeWidth={10}
            styles={buildStyles({
              pathColor: getScoreColor(currentScore),
              textColor: 'var(--tri-ink)',
              trailColor: 'var(--tri-track)',
              textSize: '24px',
            })}
          />
        </div>

        <div className="asset-rating">
          <span
            className="rating-badge"
            style={{ backgroundColor: getScoreColor(currentScore) }}
          >
            {score?.rating || 'Pending'}
          </span>
        </div>

        {/* Score Improvement Guidance */}
        <div className={`score-guidance ${hasAIGuidance ? 'score-guidance--ai' : ''}`}>
          <div className="guidance-target">
            <span className="target-label">Target:</span>
            <span className="target-value">{target}</span>
            <span className="target-rating">({targetRating})</span>
          </div>
          <p className="guidance-summary">{summary}</p>
        </div>
      </div>
    </div>
  );
};
